import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { parseAccordionIcon } from '../editor/extensions/accordion/accordion-icon.util';

export interface GojsIconData {
  /** Emoji character for go.TextBlock */
  icon_text: string;
  /** Image/PNG data URL for go.Picture (FA icons rendered to canvas, or image URL) */
  icon_src: string;
}

const EMPTY: GojsIconData = { icon_text: '', icon_src: '' };

/** Cache rendered FA icon data URLs to avoid re-rendering */
const faCache = new Map<string, string>();

/**
 * Convert kebab-case FA icon name to camelCase export key.
 * e.g. 'pen-to-square' → 'faPenToSquare'
 */
function kebabToFaKey(name: string): string {
  return `fa${name
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join('')}`;
}

/**
 * Render an FA icon to a PNG data URL using canvas + Path2D.
 */
function renderFaToDataUrl(pathData: string, viewWidth: number, viewHeight: number, color: string): string {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return '';
  }

  const scale = Math.min(size / viewWidth, size / viewHeight) * 0.85;
  const offsetX = (size - viewWidth * scale) / 2;
  const offsetY = (size - viewHeight * scale) / 2;

  ctx.translate(offsetX, offsetY);
  ctx.scale(scale, scale);

  const path = new Path2D(pathData);
  ctx.fillStyle = color;
  ctx.fill(path);

  return canvas.toDataURL('image/png');
}

/**
 * Resolve a serialized icon string into GoJS-compatible rendering data.
 *
 * - Emoji → `icon_text` (rendered via `go.TextBlock`)
 * - FA icon → `icon_src` as PNG data URL (rendered via `go.Picture`, pre-rendered with canvas)
 * - Image → `icon_src` as image URL (rendered via `go.Picture`)
 */
export function resolveGojsIcon(serializedIcon: string | undefined): GojsIconData {
  if (!serializedIcon) {
    return EMPTY;
  }

  const parsed = parseAccordionIcon(serializedIcon);
  if (!parsed) {
    return EMPTY;
  }

  switch (parsed.type) {
    case 'emoji':
      return { icon_text: parsed.value, icon_src: '' };

    case 'fa': {
      const color = !parsed.color || parsed.color === 'currentColor' ? '#706f6d' : parsed.color;
      const cacheKey = `${parsed.value}:${color}`;
      const cached = faCache.get(cacheKey);
      if (cached) {
        return { icon_text: '', icon_src: cached };
      }

      const def = (fas as Record<string, IconDefinition>)[kebabToFaKey(parsed.value)];
      if (!def?.icon) {
        return EMPTY;
      }

      const [width, height, , , path] = def.icon;
      const svgPath = Array.isArray(path) ? path.join(' ') : path;
      const dataUrl = renderFaToDataUrl(svgPath, width, height, color);
      if (dataUrl) {
        faCache.set(cacheKey, dataUrl);
      }
      return { icon_text: '', icon_src: dataUrl };
    }

    case 'img':
      return { icon_text: '', icon_src: parsed.value };
  }
}
